import { useContext } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import LabelValuePair, {
  OptionalLabelValuePair,
} from "../../components/LabelValuePair";
import { capitalize, formatAddress, newlinesToJSX } from "../../lib/display";
import { HouseholdContext } from "./HouseholdInfo";

/**
 * Render one `additionalFields` value. A Slant `url`-typed field becomes a
 * link; every other type — and any field from a CRM that reports no type at all
 * — renders as the plain string it already was (APP-1416). `number` and `date`
 * arrive already display-formatted from the API-side join, so only `url` needs
 * markup here.
 *
 * The type is what decides, never the value: Slant firms type URL-looking
 * fields as plain `text` (slant-mock's `sharefile_reporting_url` is one), and
 * those must stay unlinked.
 *
 * Only http(s) is linked — the value comes from the CRM, and an `href` carrying
 * another scheme (`javascript:`) would execute on click.
 */
const renderAdditionalField = (value: string, fieldType?: string) =>
  fieldType === "url" && /^https?:\/\//i.test(value) ? (
    <a href={value} rel="noreferrer" target="_blank">
      {value}
    </a>
  ) : (
    value
  );

const HouseholdProfile = () => {
  const { household } = useContext(HouseholdContext);

  // A household's members render once, from the canonical `members` list below
  // (AC contacts; their Slant linkage comes from contact mapping), exactly like
  // Wealthbox/Redtail. The separate read-only "Slant CRM" section that live-read
  // the Slant household's people was removed (APP-1316) — it duplicated members
  // already shown here. Slant people not yet mapped to an AC contact don't
  // appear until mapped (no on-profile import; that is the migration tool's job).
  return (
    <Row>
      <Col xl={6} lg={8} xs={12}>
        <h3>Contact Info</h3>
        {/* These two read `HouseholdContactInfo`
            (`api/src/households-base/households-base.service.ts`), whose nine
            keys are always present and `null` when the CRM has no value
            (APP-1648), so `== null` is the test here. */}
        {household?.contactInfo.phoneNumber == null ||
        household?.contactInfo.phoneNumber === "" ? null : (
          <LabelValuePair
            label="Primary Phone"
            value={
              <a href={`tel:${household?.contactInfo.phoneNumber}`}>
                {household?.contactInfo.phoneNumber}
              </a>
            }
            orientation="horizontal"
            widths={{ label: { xxl: 4, md: 6 } }}
            plaintext
          />
        )}
        {household?.contactInfo.email == null ||
        household?.contactInfo.email === "" ? null : (
          <LabelValuePair
            label="Primary Email"
            value={
              <a href={`mailto:${household?.contactInfo.email}`}>
                {household?.contactInfo.email}
              </a>
            }
            orientation="horizontal"
            widths={{ label: { xxl: 4, md: 6 } }}
            plaintext
          />
        )}
        <LabelValuePair
          label="Primary Address"
          value={newlinesToJSX(formatAddress(household?.contactInfo ?? {}))}
          orientation="horizontal"
          widths={{ label: { xxl: 4, md: 6 } }}
          plaintext
        />
        <OptionalLabelValuePair
          label="Background Info"
          value={household?.backgroundInfo}
          orientation="horizontal"
          widths={{ label: { xxl: 4, md: 6 } }}
          plaintext
        />
        {Object.entries(household?.additionalFields ?? {}).map(
          ([field, value]) => (
            <OptionalLabelValuePair
              label={field}
              value={renderAdditionalField(
                value,
                household?.additionalFieldTypes?.[field],
              )}
              orientation="horizontal"
              widths={{ label: { xxl: 4, md: 6 } }}
              plaintext
            />
          ),
        )}
      </Col>
      {(household?.members ?? []).length <= 0 ? null : (
        <Col xl={6} lg={4} xs={12}>
          <h3>Members</h3>
          {(household?.members ?? []).map((member) => (
            <Card key={member.id} className="mb-3">
              <Card.Header as="h5">
                <Link to={`/clients/contacts/${member.id}`}>{member.name}</Link>
              </Card.Header>
              <Card.Body>
                <OptionalLabelValuePair
                  label="Title"
                  value={capitalize(member.title ?? "")}
                  orientation="horizontal"
                  plaintext
                />
                {/* `== null`, not `typeof … === "undefined"`: a member is a
                    `Contact`, whose `phoneNumber` and `email` are
                    `string | null` (APP-1547), and `typeof null` is
                    `"object"`. The `typeof` form would render a `tel:null`
                    link for a member with no phone number. */}
                {member.phoneNumber == null ||
                member.phoneNumber === "" ? null : (
                  <LabelValuePair
                    label="Phone Number"
                    value={
                      <a href={`tel:${member.phoneNumber}`}>
                        {member.phoneNumber}
                      </a>
                    }
                    orientation="horizontal"
                    plaintext
                  />
                )}
                {member.email == null || member.email === "" ? null : (
                  <OptionalLabelValuePair
                    label="Email"
                    value={
                      <a href={`mailto:${member.email}`}>{member.email}</a>
                    }
                    orientation="horizontal"
                    plaintext
                  />
                )}
                <OptionalLabelValuePair
                  label="Birth Date"
                  value={member.birthDate?.toLocaleDateString("en-US", {
                    timeZone: "UTC",
                  })}
                  orientation="horizontal"
                  plaintext
                />
                <OptionalLabelValuePair
                  label="Retirement Date"
                  value={member.retirementDate?.toLocaleDateString("en-US", {
                    timeZone: "UTC",
                  })}
                  orientation="horizontal"
                  plaintext
                />
                <OptionalLabelValuePair
                  label="Date of Death"
                  value={member.dateOfDeath?.toLocaleDateString("en-US", {
                    timeZone: "UTC",
                  })}
                  orientation="horizontal"
                  plaintext
                />
                <OptionalLabelValuePair
                  label="Background Info"
                  value={member.backgroundInfo}
                  orientation="horizontal"
                  plaintext
                />
                {Object.entries(member.additionalFields ?? {}).map(
                  ([field, value]) => (
                    <OptionalLabelValuePair
                      label={field}
                      value={renderAdditionalField(
                        value,
                        member.additionalFieldTypes?.[field],
                      )}
                      orientation="horizontal"
                      plaintext
                    />
                  ),
                )}
              </Card.Body>
            </Card>
          ))}
        </Col>
      )}
    </Row>
  );
};

export default HouseholdProfile;
